import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { Modal } from "@gpstrackit/gtc-react-controls";
import moment from "moment";
import { Toolbar, ExportButton } from "Components/TableView";
import { Card, Typography, Box, CardContent, Grid } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { NotificationManager } from "Components/Notifications";

import {
  TimeFrame,
  useTimeFrameComponent,
} from "@gpstrackit/gtc-react-controls";
//actions
import {
  reset,
  accountSearchWebHooks,
  accountSearchWebHooksLog,
} from "../../redux/slice";

import "./styles.scss";
import { sendGenericExport, clearRedux } from "Redux/actions";

const LogWebHooks = (props) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const { refresh, getData } = props;
  const { openLog, loading, webHookLogs, webHookLogSelected } = useSelector((state) => state.accountWebHooksRedux);
  const [errorMessage, setErrorMessage] = useState("");
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const timeFrameOptions = [
    { id: "today", label: messages["date_last_today"] },
    { id: "yesterday", label: messages["date_last_yesterday"] },
    { id: "last7days", label: messages["date_last_7days"] },
    { id: "last15days", label: messages["date_last_15days"] },
    { id: "last30Days", label: messages["date_last_30days"] },
    { id: "custom", label: messages["date_custom"] },
  ];

  const timeFrame = useTimeFrameComponent(timeFrameOptions, "last7days");

  useEffect(() => {
    if (webHookLogSelected?.id && timeFrame?.startDate) {
      const { startDate, endDate } = timeFrame;
      dispatch(
        accountSearchWebHooksLog({
          webhookId: webHookLogSelected?.id,
          startDate,
          endDate,
        })
      );
    }
  }, [webHookLogSelected, timeFrame?.startDate, timeFrame?.endDate]);

  useEffect(() => {
    if(webHookLogSelected && openLog){
      timeFrame.reset()
    }
  }, [openLog]);

  useEffect(() => {
    if (exportResult && exportName === "webhookLog") {
      NotificationManager.info(
        messages["exportInProcessBody"],
        messages["exportInProcessTitle"],
        4000,
        null,
        null,
        "info",
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName]);

  const handleClose = useCallback(() => {
    dispatch(reset());
    getData();
    setErrorMessage("");
  });

  const DataCard = ({ occurrences, error, lastMessageError, date }) => {
    let newDate = date.split("T")
    return (
      <Card sx={{ mb: 1 }}>
        <CardContent className="webHookCard">
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="h6" component="div">
              {messages["accountWebHooks_Ocurrences"]}: {occurrences}
            </Typography>
            {error > 0 && <WarningIcon color="warning" sx={{ ml: 1 }} />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {messages["videoClipRequest_dates"]}{" "}
            {newDate[0]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {messages["accountWebHooks_Errors"]}: {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {messages["accountWebHooks_LastMessage"]}:
          </Typography>
          <Typography variant="body2" color="text.primary">
            {lastMessageError}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const DataList = ({ data }) => {
    return (
      <Box sx={{ p: 1 }}>
        {data?.length > 0 &&
          data.map((item, index) => (
            <DataCard
              key={index}
              occurrences={item.occurrences}
              error={item.error}
              lastMessageError={item.lastMessageError}
              date={item.date}
            />
          ))}
      </Box>
    );
  };

  const onExport = (type) => {
    const { startDate, endDate } = timeFrame;
    const headers = [
      {
        header: messages["accountWebHooks_Ocurrences"],
        column: "occurrences",
        functionString: "return row?.occurrences",
      },
      {
        header: messages["accountWebHooks_Errors"],
        column: "error",
        functionString: "return row?.error",
      },
      {
        header: messages["accountWebHooks_Date"],
        column: "date",
        functionString: 'return moment(row?.date).format("YYYY-MM-DD") || ""',
      },
      {
        header: messages["accountWebHooks_LastMessage"],
        column: "lastMessageError",
        functionString: "return row?.lastMessageError",
      },
    ];

    dispatch(
      sendGenericExport(
        "webhookLog",
        type == "CSV" ? "Csv" : "Pdf",
        `
      query searchWebhookLog ($conditions: [WebhookLogSearchInput], $sort: [WebhookLogInput], $limit: Int, $offset: Int) {
        searchWebhookLog (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
            items {
                error
                occurrences
                lastMessageError
                date
            }
            total
        }
    }`,
        {
          conditions: [
            {
              fields: ["webhookId"],
              terms: [webHookLogSelected?.id],
            },
            {
              range: {
                fields: "date",
                GTE: `${moment(startDate).format("YYYY-MM-DD")}T00:00:00Z`,
                LTE: `${moment(endDate).format("YYYY-MM-DD")}T23:59:59Z`,
              },
            },
          ],
        },
        headers,
        { unit: "pt", size: "A4", orientation: "portrait", fontSize: 10 },
        [],
        false,
        true
      )
    );
    setTimeout(()=>{dispatch(reset())},600)
  };

  return (
    <Modal
      title={messages["accountWebHooks_LogTitle"]}
      open={openLog}
      loading={loading}
      position="right"
      size="small"
      handleClose={handleClose}
    >
      <div style={{ padding: 16 }}>
        <Grid container>
          <Grid item sm={1}>
            <ExportButton onExport={onExport} className="webHookExportButton" />
          </Grid>
          <Grid item sm={11}>
            <div className="login-history-input-date">
              <TimeFrame
                {...timeFrame}
                placeholder={messages["reportsMainRT_timeFrame"]}
                customRangeDate={30}
                maxDate={moment()}
                filterSwitch={true}
                withTime={false}
                minDate={moment().subtract(60, "d")}
                dateFormat={"MM/DD/YYYY"}
              />
            </div>
          </Grid>
        </Grid>

        <div style={{ marginTop: 10,height:"calc(100vh - 220px)",overflow:"auto"}}>
          <DataList data={webHookLogs?.items} />
        </div>
      </div>
    </Modal>
  );
};

export default LogWebHooks;
